<template>
  <div
    class="tab-list-container w-100"
    :style="{ top: stickyTop + 'px' }"
    :class="{
      preview: isPreview,
      'has-tabs': tabStructure?.tabs?.length > 1,
      'modal-open': !isPreview && deleteTabData.tabIndex != null,
      'tab-view': !isStepperVisible,
      'scroll-visible': scroll.visible,
      [layoutSetting?.spacing]: !!layoutSetting?.spacing,
    }"
    ref="tabHeaderRef"
  >
    <div
      class="scroll-arrows left"
      :class="{
        disabled: !scroll.left,
        expand: isStepperVisible && isStepperLabel && isStepperNumber,
      }"
      v-if="scroll.visible"
      @click="onScrollClick('left')"
      @dragover.prevent="scrollOnArrowHover('left')"
      @dragleave="stopScrollOnArrowHover"
      @drop="stopScrollOnArrowHover"
    >
      <img src="../../assets/img/tab-scroll-left-arrow.svg" />
    </div>

    <div
      class="tab-scroll-container"
      ref="scrollContainer"
      @scroll="checkScrollDirection"
    >
      <div
        v-if="!isStepperVisible"
        class="drag-hover-line left position-absolute first-element"
        :class="{
          active: draggedTabIndex !== 0 && 0 === draggedOverTabIndex,
        }"
      ></div>
      <draggable
        v-model="tabStructure.tabs"
        draggable=".is-dragabble"
        handle=".tabs"
        tag="div"
        ghost-class="ghost"
        class="tab-list-wrapper d-flex align-items-center gap-4 w-100"
        @start="onTabDragStart"
        @end="onTabDragEnd"
        :move="onTabDragMove"
        :disabled="isPreview"
        ref="tabListWrapperRef"
      >
        <template v-if="tabStructure?.tabs?.length > 1">
          <div
            v-for="(tab, index) in tabStructure?.tabs"
            class="tabs h4 m-0 position-relative px-2 fw-bold fs-5"
            :class="{
              active: index == tabData.activeTabIndex,
              'dragged-over':
                getDraggedItemTabIndex !== index &&
                index == itemDragOverTabIndex,
              first: index == 0,
              last: index == tabStructure?.tabs?.length - 1,
              completed: isStepperVisible && index < tabData.activeTabIndex,
              stepper: isStepperVisible,
              'line-progress': isStepperVisible && !isStepperNumber,
              'label-wrapper': isStepperLabel,
              'is-dragabble': !isStepperVisible,
              disabled: index > tabData.activeTabIndex,
            }"
            @click.prevent.stop="onTabClick(index)"
            :key="`${index}-${tab.label}`"
            @dragover.prevent="onTabDragOver(index)"
            @dragleave="onTabDragLeave()"
            @drop="onTabItemDrop(index)"
            :ref="`tabRef${index}`"
          >
            <div
              class="step-line-wrapper"
              v-if="isStepperVisible && !isStepperNumber"
            >
              <p
                v-show="isStepperLabel"
                :contenteditable="!isPreview && tabData.renameTabIndex == index"
                @keydown.enter="(e) => e.preventDefault()"
                @blur="onRenameTab($event, tab)"
                @dblclick.prevent.stop="onLabelDoubleClick($event, index)"
                class="m-0 line tab-label"
                :class="{ truncate: tabData.renameTabIndex != index }"
                :style="getBrandedFontStyle"
                v-b-tooltip.hover="{
                  ...labelTooltip(`labelRef${index}`),
                  title: tab.label,
                }"
                :ref="`labelRef${index}`"
              >
                {{ tab?.label }}
              </p>
            </div>

            <!-- ///////////////////////////////////////////////////// -->

            <div class="step" v-if="isStepperVisible && isStepperNumber">
              <div class="step-label-container position-relative">
                <div class="step-label">
                  {{ index + 1 }}
                </div>
                <div
                  v-if="!isPreview"
                  class="remove-tab-icon position-absolute top-50 translate-middle-y"
                  @click.stop="onRemoveTab(index, tab)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                  >
                    <circle cx="5" cy="5" r="5" fill="#7F7F7F" />
                    <path
                      d="M3 3L7 7M3 7L7 3"
                      stroke="white"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <p
                v-if="isStepperLabel"
                :contenteditable="!isPreview && tabData.renameTabIndex == index"
                @keydown.enter="(e) => e.preventDefault()"
                @blur="onRenameTab($event, tab)"
                @dblclick.prevent.stop="onLabelDoubleClick($event, index)"
                class="m-0 number tab-label"
                :class="{ truncate: tabData.renameTabIndex != index }"
                :style="getBrandedFontStyle"
                @input="calculateMargin(index)"
                v-b-tooltip.hover="{
                  ...labelTooltip(`labelRef${index}`),
                  title: tab.label,
                  customClass: 'tab-label-tooltip bottom',
                }"
                :ref="`labelRef${index}`"
              >
                {{ tab?.label }}
              </p>
            </div>

            <!-- ///////////////////////////////////////////////////// -->

            <div
              v-if="!isStepperVisible"
              class="drag-hover-line position-absolute"
              :class="{
                active:
                  draggedTabIndex !== index &&
                  index === draggedOverTabIndex &&
                  draggedPosition !== null,
                [draggedPosition]: draggedPosition !== null,
              }"
            ></div>

            <p
              v-if="!isStepperVisible"
              :contenteditable="!isPreview && tabData.renameTabIndex == index"
              @keydown.enter="(e) => e.preventDefault()"
              @blur="onRenameTab($event, tab)"
              @dblclick.prevent.stop="onLabelDoubleClick($event, index)"
              class="tab-label m-0"
              :class="{ truncate: tabData.renameTabIndex != index }"
              :style="getBrandedFontStyle"
              v-b-tooltip.hover="{
                ...labelTooltip(`labelRef${index}`),
                title: tab.label,
              }"
              :ref="`labelRef${index}`"
            >
              {{ tab?.label }}
            </p>

            <div
              v-if="isCloseIconVisible"
              class="remove-tab-icon position-absolute top-50 translate-middle-y"
              @click.stop="onRemoveTab(index, tab)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <circle cx="5" cy="5" r="5" fill="#7F7F7F" />
                <path
                  d="M3 3L7 7M3 7L7 3"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </template>
        <div class="tab-tools" v-if="!isPreview">
          <div class="create-new-tab position-relative">
            <button
              class="px-4 py-2 rounded-1"
              @click.stop.prevent="onAddTab"
              type="button"
            >
              <img src="@/assets/img/tab-tool-add.svg" />
            </button>
          </div>

          <div
            class="tab-settings"
            @click="$emit('toggleSettings')"
            v-if="tabStructure.tabs.length > 1"
          >
            <img src="@/assets/img/tab-tool-settings.svg" />
          </div>
        </div>
      </draggable>
    </div>

    <div
      class="scroll-arrows right"
      :class="{
        disabled: !scroll.right,
        expand: isStepperVisible && isStepperLabel && isStepperNumber,
      }"
      v-if="scroll.visible"
      @click="onScrollClick('right')"
      @dragover.prevent="scrollOnArrowHover('right')"
      @dragleave="stopScrollOnArrowHover"
      @drop="stopScrollOnArrowHover"
    >
      <img src="@/assets/img/tab-scroll-right-arrow.svg" />
    </div>

    <DeleteTabModal
      v-if="!isPreview && deleteTabData.tabIndex != null"
      :tabName="deleteTabData.tabName"
      :disabledKeys="getDeleteModalDisabledKeys"
      :deleteModalMoveText="getDeleteModalMoveText"
      @action="onDeleteTabAction"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { generateStructureObject } from '@/utils/helpers/WorkFlowHelpers';
import DeleteTabModal from './DeleteTabModal.vue';
import { moveOrRemoveTabFromTabStructure } from '@/utils/helpers/FormBuilderHelper';

export default {
  name: 'FormBuilderTabs',
  data() {
    return {
      draggedTabIndex: null,
      draggedPosition: null,
      draggedOverTabIndex: null,
      deleteTabData: {
        tabName: null,
        tabIndex: null,
      },
      itemDragOverTabIndex: null,
      dragTimers: null,
      scroll: {
        visible: false,
        left: false,
        right: false,
        interval: null,
      },
      stickyTop: 0,
    };
  },
  props: {
    tabStructure: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    tabData: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    mutableElements: {
      type: Array,
      default: () => [],
      required: true,
    },
    isPreview: {
      type: Boolean,
      default: false,
      required: true,
    },
    isItemDragging: {
      type: Boolean,
      default: false,
      required: true,
    },
    layoutSetting: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    brandedStyle: {
      type: Object,
      default: () => ({}),
    },
    getElementsFilteredByTabs: {
      type: Function,
      default: () => {},
      required: true,
    },
    getDraggedItemTabIndex: {
      type: Number,
      default: null,
    },
    isElSidebarEditorVisible: {
      type: Boolean,
      default: false,
      required: true,
    },
    resetFormError: {
      type: Function,
      default: () => {},
      required: true,
    },
    validateSidebarElementEditor: {
      type: Function,
      required: true,
    },
  },
  methods: {
    onTabClick(tabIndex) {
      if (this.isElSidebarEditorVisible) {
        const isValid = this.validateSidebarElementEditor();
        if (!isValid) return;
        this.$emit(`toggleElSidebarEditor`);
      }
      if (this.isPreview) {
        if (tabIndex < this.tabData.activeTabIndex) {
          this.tabData.activeTabIndex = tabIndex;
          this.resetFormError();
        }
      } else {
        this.tabData.activeTabIndex = tabIndex;
      }
    },
    onAddTab() {
      if (this.isPreview) return;
      const tabsLength = this.tabStructure?.tabs?.length;
      const tabNameArr = this.tabStructure?.tabs?.map((el) => el.label);
      let tabName = null;
      let count = tabsLength + 1;

      while (tabName == null) {
        let newTabName = `Tab ${count}`;
        if (!tabNameArr.includes(newTabName)) {
          tabName = newTabName;
          break;
        }
        count++;
      }

      const tab = generateStructureObject({
        label: tabName,
      });

      this.tabStructure.tabs.push(tab);

      if (this.tabData.activeTabIndex == null) {
        this.tabData.activeTabIndex = this.tabStructure?.tabs?.length - 1 || 0;
      }
      setTimeout(() => {
        this.onScrollClick('right', 400);
      }, 1);
    },
    onRemoveTab(index, tab) {
      if (this.isPreview || this.tabStructure?.tabs?.length === 1) return;
      if (this.getElementsFilteredByTabs(tab).length === 0) {
        this.onDeleteTabAction({ action: 'delete', tabDeleteIndex: index });
        return;
      }

      this.deleteTabData = {
        tabName: tab?.label,
        tabIndex: index,
      };
    },
    onDeleteTabAction({ action, tabDeleteIndex }) {
      if (this.isPreview || this.tabStructure?.tabs?.length === 1) return;

      if (['delete', 'move'].includes(action)) {
        const deleteTabIndex = tabDeleteIndex
          ? tabDeleteIndex
          : this.deleteTabData.tabIndex;

        const candidateIndexes = [deleteTabIndex + 1, deleteTabIndex - 1];
        const newActiveTabIndex =
          candidateIndexes.find((i) => this.tabStructure.tabs[i]) ?? -1;
        const { updatedTabList, removedElementIds } =
          moveOrRemoveTabFromTabStructure({
            tabList: this.tabStructure.tabs,
            deleteTabIndex,
            targetTabIndex: action === 'move' ? newActiveTabIndex : null,
          });

        if (updatedTabList)
          this.$set(this.tabStructure, 'tabs', updatedTabList);

        if (removedElementIds?.length) {
          const filteredElements = this.mutableElements.filter(
            (element) => !removedElementIds.includes(element.id)
          );
          this.$emit(`updateMutableElements`, filteredElements);
        }

        if (this.tabData.activeTabIndex == deleteTabIndex) {
          this.tabData.activeTabIndex =
            deleteTabIndex === 0
              ? 0
              : this.tabStructure.tabs.length - 1 < deleteTabIndex
              ? deleteTabIndex - 1
              : deleteTabIndex;
        } else if (this.tabData.activeTabIndex > deleteTabIndex) {
          this.tabData.activeTabIndex = Math.max(
            0,
            this.tabData.activeTabIndex - 1
          );
        }
      }
      this.deleteTabData = {
        tabName: null,
        tabIndex: null,
      };
    },
    onRenameTab(e, tab) {
      if (this.isPreview) return;
      const renamedText = e.target.innerText?.trim();

      if (!renamedText || renamedText === '') {
        e.target.innerText = tab.label;
        return;
      }

      this.tabData.renameTabIndex = null;
      if (tab.label !== renamedText) {
        tab.label = renamedText;
      } else {
        e.target.innerText = tab.label;
      }

      e.target.scrollLeft = 0;
    },
    onLabelDoubleClick(e, index) {
      if (this.isPreview) return;
      this.tabData.renameTabIndex = index;
      setTimeout(() => {
        e.target.focus();
      });
    },
    onTabDragStart(e) {
      if (this.isPreview) return;
      if (e?.oldDraggableIndex != null || e?.oldIndex != null) {
        this.draggedTabIndex =
          e.oldDraggableIndex != null ? e.oldDraggableIndex : e.oldIndex;
      }
    },
    onTabDragMove() {
      return false;
    },
    onTabDragEnd() {
      if (this.isPreview) return;
      if (
        this.draggedTabIndex === this.draggedOverTabIndex ||
        this.draggedOverTabIndex == null
      ) {
        this.draggedTabIndex = null;
        this.draggedOverTabIndex = null;
        this.draggedPosition = null;
        return;
      }
      const tabs = Object.assign([], this.tabStructure.tabs);
      const movedTab = tabs.splice(this.draggedTabIndex, 1)[0];

      const insertAt = Math.max(
        0,
        Math.min(this.draggedOverTabIndex, tabs.length)
      );
      tabs.splice(insertAt, 0, movedTab);

      this.tabStructure.tabs = tabs;
      this.draggedTabIndex = null;
      this.draggedOverTabIndex = null;
      this.draggedPosition = null;
    },
    checkScroll() {
      const scrollContainer = this.$refs.scrollContainer;
      const tabListWrapperRef = this.$refs.tabListWrapperRef;
      if (!scrollContainer) return;

      this.scroll.visible =
        tabListWrapperRef.$el.scrollWidth > scrollContainer.offsetWidth;

      this.checkScrollDirection();
    },
    onScrollClick(direction, amount = 200) {
      const scrollContainer = this.$refs.scrollContainer;
      scrollContainer.scrollBy({
        left: direction == 'left' ? -amount : amount,
        behavior: 'smooth',
      });
      this.$nextTick(this.checkScrollDirection);
    },
    checkScrollDirection() {
      const el = this.$refs.scrollContainer;
      if (!el) return;
      const tolerance = 2;

      this.scroll.left = el.scrollLeft > tolerance;
      this.scroll.right =
        el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance;
    },
    onTabDragOver(tabIndex) {
      if (this.isPreview) return;
      if (this.isItemDragging) {
        this.itemDragOverTabIndex = tabIndex;

        if (!this.dragTimers) {
          this.dragTimers = setTimeout(() => {
            this.onTabClick(tabIndex);
            clearTimeout(this.dragTimers);
            this.dragTimers = null;
          }, 1000);
        }
      } else {
        if (this.draggedTabIndex !== null) {
          this.draggedOverTabIndex = tabIndex;
          if (tabIndex < this.draggedTabIndex) {
            this.draggedPosition = 'left';
          } else {
            this.draggedPosition = 'right';
          }
        }
      }
    },
    onTabItemDrop(tabIndex) {
      if (this.isPreview) return;
      this.$emit('onTabItemDrop', tabIndex);
      this.onTabDragLeave();
    },
    onTabDragLeave() {
      this.itemDragOverTabIndex = null;

      if (this.dragTimers) {
        clearTimeout(this.dragTimers);
        this.dragTimers = null;
      }
    },
    scrollTabToView(tabIndex) {
      if (this.scroll.visible) {
        const tab = this.$refs[`tabRef${tabIndex}`];
        if (tab && tab[0]) {
          setTimeout(() => {
            tab[0].scrollIntoView({ behavior: 'smooth', inline: 'center' });
          }, 10);
        }
      }
    },
    scrollOnArrowHover(direction) {
      if (this.isItemDragging) {
        if (this.scroll.interval) return;
        const scrollContainer = this.$refs.scrollContainer;
        this.scroll.interval = setInterval(() => {
          scrollContainer.scrollLeft += direction === 'right' ? 5 : -5;
        }, 16);
      }
    },
    stopScrollOnArrowHover() {
      clearInterval(this.scroll.interval);
      this.scroll.interval = null;
    },
    calculateMargin(tabIndex) {
      if (tabIndex == 0 && this.isStepperVisible && this.isStepperNumber) {
        this.$nextTick(() => {
          const el = this.$refs['labelRef' + tabIndex]?.[0];
          if (!el) return;

          const textWidth = el.offsetWidth;

          let margin = 75 - ((textWidth - 30) / (145 - 30)) * (75 - 23);
          margin = Math.max(23, Math.min(75, margin));

          const secondTab = this.$refs['tabRef' + 1];
          if (secondTab?.length > 0) {
            secondTab[0].style.setProperty(
              'margin-left',
              `${Math.round(margin)}px`,
              'important'
            );
          }
        });
      }
    },
    isTruncated(el) {
      return el && el.scrollWidth > el.clientWidth;
    },
    labelTooltip(ref) {
      const el = this.$refs[ref];
      let disabled = true;

      if (this.isPreview && el?.length > 0 && el[0]) {
        const isTruncated = this.isTruncated(el[0]);
        disabled = !isTruncated;
      }

      return {
        container: 'tab-container',
        customClass: 'tab-label-tooltip top',
        placement: 'top',
        disabled: disabled,
      };
    },
    calculateTop() {
      const header = this.$refs.tabHeaderRef;
      if (!header) return;

      if (this.isPreview) {
        this.stickyTop = 0;
      }

      const headerHeight = header?.offsetHeight || 0;
      this.$emit('setOverflowWrapperheight', headerHeight);
    },
  },
  computed: {
    isStepperVisible() {
      return this.tabStructure?.layout?.showProgressBar || false;
    },
    isStepperNumber() {
      return this.tabStructure?.layout?.showNumbers || false;
    },
    isStepperLabel() {
      return this.tabStructure?.layout?.showLabel || false;
    },
    getDeleteModalDisabledKeys() {
      if (
        this.tabStructure?.tabs[this.deleteTabData?.tabIndex]?.rows?.length == 0
      ) {
        return ['move'];
      }

      return [];
    },
    getBrandStyles() {
      const defaults = {
        textColor: '#000000',
        primaryColor: '#000000',
      };

      return {
        textColor: this.brandedStyle?.textColor || defaults.textColor,
        primaryColor:
          this.brandedStyle?.primaryBrandColor || defaults.primaryColor,
      };
    },
    getBrandedFontStyle() {
      return {
        fontFamily: this.brandedStyle?.fontType
          ? `${this.brandedStyle?.fontType} !important`
          : '',
      };
    },
    getDeleteModalMoveText() {
      if (
        this.deleteTabData.tabIndex === 0 &&
        this.tabStructure.tabs.length > 1
      ) {
        return 'Bringen Sie den Inhalt voran';
      }

      if (
        this.deleteTabData.tabIndex === 0 &&
        this.tabStructure.tabs.length === 1
      ) {
        return 'Registerkarte entfernen, Inhalt behalten';
      }

      return 'Inhalt zurückverschieben';
    },
    firstLabel() {
      return this.tabStructure?.tabs?.[0]?.label || '';
    },
    isCloseIconVisible() {
      if (!this.isPreview) {
        if (this.isStepperVisible) {
          if (!this.isStepperNumber) {
            return true;
          }
        } else {
          return true;
        }

        return false;
      }
      return false;
    },
  },
  mounted() {
    this.calculateTop();
    this.calculateMargin(0);
    window.addEventListener('resize', this.checkScroll);
    window.addEventListener('resize', this.calculateTop);
    setTimeout(() => {
      this.checkScroll();
    }, 0);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScroll);
    window.removeEventListener('resize', this.calculateTop);
    if (this.dragTimers) {
      clearTimeout(this.dragTimers);
      this.dragTimers = null;
    }
    if (this.scroll.interval) {
      clearInterval(this.scroll.interval);
      this.scroll.interval = null;
    }
  },
  watch: {
    'tabStructure.tabs': {
      handler() {
        setTimeout(() => {
          this.checkScroll();
        }, 0);
      },
      deep: true,
    },
    'tabStructure.layout': {
      handler(newVal) {
        setTimeout(() => {
          this.checkScroll();
        }, 0);
        const secondTab = this.$refs['tabRef' + 1]?.[0];
        if (newVal.showNumbers) {
          if (newVal.showLabel) {
            this.calculateMargin(0);
          } else {
            if (secondTab) {
              secondTab.style.removeProperty('margin-left');
            }
          }
        } else {
          if (secondTab) {
            secondTab.style.removeProperty('margin-left');
          }
        }
      },
      deep: true,
    },
    firstLabel(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.calculateMargin(0);
      }
    },
  },
  components: { draggable, DeleteTabModal },
};
</script>
<style scoped>
.tab-list-container {
  font-family: 'OpenSans-Bold';
  position: sticky;
  top: 0;
  left: 0;
  background: transparent;
  z-index: 51;
  width: 100% !important;
  max-width: 100%;
  display: flex;
  align-items: center;
}
.tab-list-container.has-tabs {
  margin-bottom: 30px;
}

.tab-list-container.modal-open {
  z-index: 52;
}

.tab-list-container.preview {
  margin-bottom: 60px !important;
  padding-left: 0px !important;
  height: 30px;
  margin-left: -1px;
}

.tab-list-container.preview:not(.has-tabs) {
  margin: 0px !important;
  height: 0px !important;
}

.tab-list-container.preview.fullwidth.scroll-visible {
  padding: 0px 20px !important;
}

.tab-list-container.preview.tab-view {
  height: unset;
}

.tab-list-container.preview.tab-view .tab-list-wrapper {
  height: 55px;
}

.tab-list-container.preview.tab-view .tabs.disabled {
  cursor: default;
}

.tab-list-container .tab-scroll-container {
  overflow-x: auto;
  position: relative;
  width: fit-content;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 6px;
}

.tab-list-container.preview:not(.scroll-visible) .tab-scroll-container {
  padding: 0px 0px !important;
}

.tab-list-container .tab-scroll-container::-webkit-scrollbar {
  display: none;
}

.tab-list-container .tab-list-wrapper {
  max-width: max-content;
}

.tab-list-container .tabs {
  cursor: pointer;
  line-height: 36px;
  border-radius: 8px;
}

.tab-list-container .tabs.active {
  cursor: default;
}

.tab-list-container .tabs.active,
.tab-list-container .tabs.completed {
  color: v-bind('getBrandStyles.textColor');
}
.tab-list-container .tabs.is-dragabble:not(.active) .tab-label {
  color: v-bind('getBrandStyles.textColor');
  opacity: 0.3;
}
.tab-list-container .tabs.dragged-over {
  background: #f2efef;
}

.tab-list-container .tabs .tab-label {
  user-select: none !important;
  max-width: 250px;
  min-width: 12px;
  overflow: hidden;
  white-space: nowrap;
  font-size: 18px;
  padding: 0px 3px;
}

.tab-list-container .tabs .truncate {
  text-overflow: ellipsis;
}

.tab-list-container .tabs .remove-tab-icon {
  right: -10px;
  font-size: 16px;
  display: none;
  transition: all 200ms ease-in-out;
  cursor: pointer;
}

.tab-list-container .tabs:hover .remove-tab-icon {
  display: block;
  opacity: 0.6;
}

.tab-list-container .tabs .remove-tab-icon:hover {
  display: block;
  opacity: 1;
}

.tab-list-container .tabs.line-progress.stepper::before {
  content: '';
  position: absolute;
  top: 20px;
  width: calc(100% - 9px);
  height: 6px;
  margin: 0 auto;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  border-radius: 10px;
}

.tab-list-container .tabs.active::before {
  content: '';
  position: absolute;
  bottom: -4px;
  width: calc(100% - 16px);
  height: 3px;
  background: v-bind('getBrandStyles.primaryColor');
  margin: 0 auto;
  left: 50%;
  transform: translateX(-50%);
}

.tab-list-container .tabs.line-progress.stepper:hover .remove-tab-icon {
  cursor: pointer !important;
  top: 0 !important;
  transform: unset !important;
  right: 10px !important;
  left: auto;
}

.tab-list-container .tabs.line-progress.stepper .remove-tab-icon svg {
  vertical-align: top !important;
}

.tab-list-container
  .tabs.line-progress:hover
  .remove-tab-icon
  .tab-list-container
  .tabs.active::before,
.tab-list-container .tabs.completed::before {
  content: '';
  position: absolute;
  bottom: -4px;
  width: calc(100% - 16px);
  height: 3px;
  background: #000000;
  margin: 0 auto;
  left: 50%;
  transform: translateX(-50%);
}

.tab-list-container.has-tabs .create-new-tab {
  opacity: 1 !important;
}

.tab-list-container .create-new-tab button {
  width: 70px;
  height: 44px;
  flex-shrink: 0;
  border: 0;
  background: #f4f4f6;
  transition: all 200ms ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none !important;
  border: none !important;
}

.tab-list-container .create-new-tab button:hover {
  background: #dedee6;
}

.tab-list-container .drag-hover-line {
  height: 30px;
  content: '';
  border-right: 5px solid #86c3f4;
  top: 5px;
  display: none;
}
.tab-list-container .tabs .drag-hover-line.left {
  left: -12px;
}

.tab-list-container .tabs .drag-hover-line.right {
  right: -12px;
}

.tab-list-container .drag-hover-line.active {
  display: block;
}

.tab-list-container .tabs:first-child::before {
  transform: unset;
  width: calc(100% - 9px);
  left: 5px;
}

.tab-list-container .tab-tools {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding-right: 10px;
}

.tab-list-container .create-new-tab {
  transition: all 200ms ease-in-out;
  opacity: 0;
}

.tab-list-container:hover
  .tab-scroll-container
  .tab-list-wrapper
  .create-new-tab {
  opacity: 1;
}

.tab-list-container .scroll-arrows {
  top: 0;
  height: 44px;
  display: flex;
  align-items: center;
  cursor: pointer;
  min-width: 30px;
  width: 30px;
  background-color: transparent;
  z-index: 1;
}

.tab-list-container .scroll-arrows.expand {
  height: 61px;
}

.tab-list-container .scroll-arrows img {
  width: 8px;
  height: 14px;
}

.tab-list-container .scroll-arrows.left {
  left: -7px;
  justify-content: start;
  border-right: 1px solid #c0c0c0;
}

.tab-list-container .scroll-arrows.right {
  right: -18px;
  border-left: 1px solid #c0c0c0;
  justify-content: end;
}

.tab-list-container .scroll-arrows.disabled {
  cursor: default;
}

.tab-list-container .stepper {
  font-family: sans-serif;
  font-size: 1rem;
  position: relative;
  counter-reset: step 0;
  min-width: 205px;
  display: flex;
  flex-flow: row nowrap;
  gap: 0;
  justify-content: space-around;
  align-items: flex-start;
}
.tab-list-container .stepper:hover .remove-tab-icon {
  right: -14px;
  top: -0 !important;
  transform: translateX(-50%) !important;
  height: 10px;
}

.tab-list-container .stepper:hover .remove-tab-icon svg {
  vertical-align: top;
}

.tab-list-container .stepper .step {
  position: relative;
  counter-increment: step;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
}

.tab-list-container .stepper .step::before {
  position: absolute;
  content: '';
  width: calc(100% - 39px);
  height: 4px;
  left: -12px;
  top: 20px;
  z-index: -1;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
}

.tab-list-container
  .tabs.last.stepper:not(.label-wrapper, .line-progress)
  .step::before {
  width: 150px;
}

.tab-list-container .tabs.stepper:not(.active, .completed) .step::before {
  background: v-bind('getBrandStyles.primaryColor');
  opacity: 0.3;
}

.tab-list-container .tab-list-wrapper:has(.stepper) {
  gap: 8px !important;
  align-items: start !important;
}
.tab-list-container .stepper.first .step::before {
  display: none;
}

.tab-list-container .stepper.active .step::before,
.tab-list-container .stepper.completed .step::before {
  background: v-bind('getBrandStyles.primaryColor');
}

.tab-list-container .stepper .step .step-label {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 50%;
  margin: 0 auto;
  border: 3px solid;
  background: white;
}

.tab-list-container
  .stepper
  .step
  .step-label
  .tab-list-container
  .stepper
  .step
  .tab-label {
  font-size: 14px;
  color: #999;
  margin-top: 0.5rem;
  hyphens: auto;
  text-align: center;
  line-height: 36px;
  font-weight: 600;
  white-space: nowrap;
}

.tab-list-container .tabs.stepper:not(.active, .completed) .step .step-label {
  color: v-bind('getBrandStyles.primaryColor');
  border-color: v-bind('getBrandStyles.primaryColor');
  opacity: 0.3;
}

/* line */

.tab-list-container .tab-list-wrapper:has(.label-wrapper) {
  gap: 8px !important;
}
.tab-list-container .tabs.label-wrapper.line-progress {
  min-width: 191px;
  max-width: 191px;
  padding: 0 !important;
  height: auto;
}
.tab-list-container .tabs.label-wrapper.line-progress:hover .remove-tab-icon {
  top: 0 !important;
}

.tab-list-container .tabs.label-wrapper.line-progress::before {
  width: calc(100% - 9px);
  max-width: 100%;
  top: unset !important;
  bottom: -4px;
}

.tab-list-container .tabs.label-wrapper.line-progress .tab-label {
  max-width: 190px;
}

.tab-list-container .tabs.line-progress.stepper {
  min-width: 200px;
  max-width: 200px;
  height: 44px;
}
.tab-list-container .tabs.line-progress.stepper.active:before,
.tab-list-container .tabs.line-progress.stepper.completed:before {
  height: 6px;
  border-radius: 10px;
  top: unset;
}
.tab-list-container .tabs.line-progress.active.stepper:before,
.tab-list-container .tabs.line-progress.completed.stepper:before {
  top: 20px;
  background: v-bind('getBrandStyles.primaryColor');
  opacity: 1;
}

.tab-list-container
  .tabs.line-progress.stepper:not(.active, .completed):before {
  background: v-bind('getBrandStyles.primaryColor');
  opacity: 0.3;
}
.tab-list-container
  .tabs.line-progress.stepper.label-wrapper:not(.active, .completed)
  .step-line-wrapper
  .tab-label {
  color: v-bind('getBrandStyles.textColor');
  opacity: 0.3;
}

.tab-list-container .tab-list-wrapper:has(.line-progress) {
  gap: 0 !important;
}

.tab-list-container .tabs.active .stepper .step:before,
.tab-list-container .tabs.completed .step:before {
  background: v-bind('getBrandStyles.primaryColor');
}
.tab-list-container .tabs.active .step .step-label,
.tab-list-container .tabs.completed .step .step-label {
  background: v-bind('getBrandStyles.primaryColor');
  color: #fff;
  border-color: v-bind('getBrandStyles.primaryColor');
}

.tab-list-container .tabs.active .step .tab-label,
.tab-list-container .tabs.completed .step .tab-label {
  color: v-bind('getBrandStyles.textColor');
}
.tab-list-container .tabs:not(.active, .completed) .step .tab-label {
  color: v-bind('getBrandStyles.textColor');
  opacity: 0.3;
}
.tab-list-container .tabs .step .tab-label {
  max-width: 145px;
}
.tab-list-container .tab-settings img {
  opacity: 0.6;
  cursor: pointer;
  transition: all 200ms ease-in-out;
}
.tab-list-container .tab-settings:hover img {
  opacity: 1;
}

.tab-list-container .tabs.line-progress.stepper:has(.step-line-wrapper):before {
  display: block;
  bottom: 0;
}
.tab-list-container .tabs.stepper:before {
  display: none;
}

.tab-list-container.preview:not(.scroll-visible) .tabs.first.is-dragabble {
  padding-left: 0px !important;
}

.tab-list-container.preview:not(.scroll-visible)
  .tabs.first.is-dragabble::before {
  left: 0 !important;
}

.tab-list-container.preview .tabs.first.line-progress.stepper::before {
  left: 96px;
}

.tab-list-container
  .tabs.first.stepper:not(.label-wrapper, .line-progress)
  .step-label {
  margin-left: 0px;
}

.tab-list-container .tabs.first.stepper:not(.label-wrapper, .line-progress) {
  min-width: 131px !important;
}

.tab-list-container .tabs.first.stepper:not(.line-progress) {
  width: fit-content !important;
  min-width: unset !important;
}

.tab-list-container .tabs.first.stepper:not(.line-progress) .step-label {
  margin: 0px;
}

.tab-list-container
  .tab-list-wrapper
  .tabs:not(.line-progress, .is-dragabble):nth-of-type(2) {
  margin-left: 75px !important;
}

.tab-list-container
  .tabs.stepper:not(.line-progress)
  .step-label
  .remove-tab-icon {
  height: 10px !important;
  right: -20px !important;
  opacity: 1 !important;
}

.tab-list-container .tab-scroll-container .drag-hover-line.first-element {
  top: 9px !important;
  left: 0px;
}

.tab-list-container .tabs.last.stepper:not(.label-wrapper, .line-progress) {
  width: fit-content !important;
  min-width: unset !important;
}

.tab-list-container
  .tabs.last.stepper:not(.label-wrapper, .line-progress)
  .step-label-container {
  margin-left: 75px !important;
}

.tab-label-tooltip {
  position: absolute;
  border-radius: 7px;
  opacity: 1;
  background: #1e1e1e;
  box-shadow: 0px 0px 5px 3px rgba(0, 0, 0, 0.15);
}

.tab-label-tooltip.top {
  top: -40px !important;
}

.tab-label-tooltip.bottom {
  top: 60px !important;
}

.tab-label-tooltip ::v-deep .tooltip-inner {
  color: #e8e8e8;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  padding: 10px;
}
</style>
